import React from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButtonProps,
} from '@mui/material';
import { CompanyPhoneType } from 'src/modules/companies/api/CompanyPhoneApi';
import { CompanyPhonesTabViewModel } from 'src/modules/companies/types';
import { OrderStatus, phoneRowActionSx, tabActionButtonSx } from 'src/modules/companies/helpers';
import SetupHostedNumberDialog from '../SetupHostedNumberDialog';

const CompanyPhonesTabView = (props: CompanyPhonesTabViewModel) => {
  const {
    companyId,
    phoneNumbers,
    phoneErrors,
    setupHostedNumberDialogOpen,
    setupHostedNumberPhone,
    setupHostedNumberExistingOrder,
    getPhoneOrderState,
    onPhoneNumberChange,
    onPhoneNumberTypeChange,
    onPhoneNumberDelete,
    onAddNewPhoneNumber,
    onOpenSetupHostedNumberDialog,
    onCloseSetupHostedNumberDialog,
    onSave,
  } = props;

  const renderSetupHostedNumberButton = (phone: { id: string; number: string; isNew?: boolean }) => {
    const { status, description } = getPhoneOrderState(phone.number);

    if (status === OrderStatus.successful) {
      return (
        <Tooltip title="Phone number setup successfully completed">
          <IconButton
            sx={phoneRowActionSx}
            color="success"
            disabled
          >
            <CheckCircleIcon />
          </IconButton>
        </Tooltip>
      );
    }

    let color: IconButtonProps['color'] = "primary";
    let icon = <SettingsIcon />;
    if (status === OrderStatus.failed) {
      color = "error";
      icon = <ErrorOutlineIcon />;
    } else {
      switch (status) {
        case OrderStatus.in_progress:
          icon = <PendingIcon />;
          break;
        case OrderStatus.pending:
          icon = <SettingsIcon />;
          break;
        default:
          break;
      }
    }

    return (
      <Tooltip title={description}>
        <IconButton
          sx={phoneRowActionSx}
          color={color}
          onClick={() => onOpenSetupHostedNumberDialog(phone)}
        >
          {icon}
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box width="70%">
      {phoneNumbers && (
        <Box mt={2.5}>
          {phoneNumbers
            .filter((p) => !p.isDeleted)
            .map((p) => (
              <Stack key={p.id} direction="row" width="100%" mt={2.5} alignItems="center" gap={1.25}>
                <Stack flex={1}>
                  <TextField
                    id="outlined-basic"
                    label={"Phone"}
                    name={p.id}
                    inputProps={{
                      pattern: "d*",
                      min: 10,
                      maxLength: 10,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                    }}
                    variant="outlined"
                    value={p.number?.replace("+1", "")}
                    onChange={onPhoneNumberChange}
                  />
                  <Box height={18} />
                </Stack>
                <Stack minWidth={160}>
                  <FormControl error={!!phoneErrors[p.id]}>
                    <InputLabel id={`phone-type-label-${p.id}`}>Type</InputLabel>
                    <Select
                      labelId={`phone-type-label-${p.id}`}
                      id={`phone-type-select-${p.id}`}
                      label={"Type"}
                      value={p.type || ''}
                      onChange={(event) => onPhoneNumberTypeChange(event, p.id)}
                    >
                      <MenuItem value={CompanyPhoneType.toll_free}>Toll Free</MenuItem>
                      <MenuItem value={CompanyPhoneType._10dlc_local}>10DLC Local</MenuItem>
                      <MenuItem value={CompanyPhoneType._10dlc_hosted}>10DLC Hosted</MenuItem>
                    </Select>
                  </FormControl>
                  <Stack direction="row" height={18} alignItems="center">
                    {phoneErrors[p.id] && (
                      <Typography variant="caption" component="div" color="error.main" ml={1.75}>
                        {phoneErrors[p.id]}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
                <IconButton
                  sx={phoneRowActionSx}
                  onClick={() => {
                    onPhoneNumberDelete(p.id);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
                <Stack direction="row" width={48} alignItems="flex-start" justifyContent="center">
                  {p.type === CompanyPhoneType._10dlc_hosted && !p.isNew && renderSetupHostedNumberButton(p)}
                </Stack>
              </Stack>
            ))}
          <Stack direction="row" gap={1.25}>
            <Button
              size="small"
              variant="contained"
              sx={tabActionButtonSx}
              onClick={onAddNewPhoneNumber}
            >
              Add New Phone
            </Button>
            <Button
              size="small"
              variant="contained"
              sx={tabActionButtonSx}
              onClick={onSave}
            >
              Save
            </Button>
          </Stack>
        </Box>
      )}
      {setupHostedNumberPhone && (
        <SetupHostedNumberDialog
          open={setupHostedNumberDialogOpen}
          onClose={onCloseSetupHostedNumberDialog}
          companyId={companyId}
          phoneId={setupHostedNumberPhone.id}
          phoneNumber={setupHostedNumberPhone.number}
          existingOrder={setupHostedNumberExistingOrder}
        />
      )}
    </Box>
  );
};

export default CompanyPhonesTabView;
